import ShowToast from '@/Components/Toast/showToastTemplate';
import { Button } from '@/Components/UI/Button/Button';
import TextInput from '@/Components/UI/Form/TextInput/TextInput';
import Modal from '@/Components/UI/Modal/Modal';
import { post } from '@/Utils/API';
import {
  MODAL_INVITE_TEAM,
  TOAST_TEXT_UNABLE_TO_INVITE_USERS,
  TOAST_TYPE_ERROR,
} from '@/Utils/constants';
import { isValidEmail } from '@/Utils/isValidEmail';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import withModal from '../withModalHOC';

const InviteTeamPopup = ({ show, hideModal }) => {
  const [invitedUsers, setInvitedUsers] = useState([
    { id: Math.random(), value: '', valid: true },
    { id: Math.random(), value: '', valid: true },
  ]);

  const [isInviteSent, setIsInviteSent] = useState(false);
  const [invalidEmailPresent, setInvalidEmailPresent] = useState(false);
  const [enableSendInvites, setIsEnabledSendInvites] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  const validateInviteEmail = () => {
    const validateEmails = invitedUsers.map((user) => {
      const { value: email } = user || {};
      if (email.trim() !== '' && !isValidEmail(email)) {
        return { ...user, valid: false };
      } else return { ...user, valid: true };
    });

    setInvitedUsers(validateEmails);
    setInvalidEmailPresent(true);
    setResponseMessage('');
  };

  const handleInviteUsers = (e, i) => {
    const newList = [...invitedUsers];
    newList[i].value = e.target.value;
    newList[i].valid = true;

    setInvalidEmailPresent(false);
    setInvitedUsers(newList);
  };

  const inviteUsers = async () => {
    let recipients;
    let multipleInvites;
    const endpoint = 'send-invite';

    if (invitedUsers?.filter((i) => i.value.length !== 0).length === 0) {
      hideModal();
    } else {
      try {
        recipients = invitedUsers?.map((i) => i?.value.trim()).filter((i) => i);
        multipleInvites = invitedUsers?.filter((i) => i?.value).length > 1;

        const res = await post(endpoint, { recipients });

        if (res.Success) {
          setResponseMessage(`Your invite${multipleInvites ? 's have' : ' has'} been sent!`);
          setIsInviteSent(true);
        } else {
          setResponseMessage(
            `Sorry, we could not send ${
              multipleInvites ? 'these individuals' : 'this individual'
            } an invite.`
          );
          setIsInviteSent(false);
        }
      } catch (error) {
        ShowToast({
          type: TOAST_TYPE_ERROR,
          text: TOAST_TEXT_UNABLE_TO_INVITE_USERS,
          endpoint: endpoint,
          payload: { recipients },
          errorContext: error,
          fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
        });
      }
    }
  };

  const addInput = () => {
    setInvitedUsers((users) => {
      return [
        ...users,
        {
          id: Math.random(),
          value: '',
          valid: true,
        },
      ];
    });
  };

  const removeInput = (id) => setInvitedUsers(invitedUsers.filter((i) => i.id !== id));

  useEffect(() => {
    setInvitedUsers(invitedUsers);
  }, [invalidEmailPresent]);

  useEffect(() => {
    const invalidEmailCount = invitedUsers.filter(({ valid }) => !valid).length !== 0;
    const inviteCount = invitedUsers?.filter((i) => i.value.length !== 0).length === 0;
    setIsEnabledSendInvites(inviteCount || invalidEmailCount);
  }, [invitedUsers]);

  return (
    <Modal
      isOpen={show}
      onClose={hideModal}
      size='medium'
      iconName='users-three'
      title='Invite Your Team'
      subhead={"The big data party has started and everyone's invited. Let's light this fire."}
    >
      <div className={clsx('overflow-auto')}>
        <div className='px-6 pb-6'>
          {invitedUsers?.map(({ id, valid }, i) => {
            return (
              <div className='flex flex-col' key={id}>
                <div className='flex items-center justify-center gap-1'>
                  <TextInput
                    className={clsx(
                      '!text-sm rounded w-full ease-in-out transition-shadow duration-200 my-1',
                      !valid && '!border !border-violet-web-600 shadow-md !shadow-violet-web-100'
                    )}
                    disabled={isInviteSent}
                    placeholder={'Enter email address'}
                    onChange={(e) => handleInviteUsers(e, i)}
                    onBlur={validateInviteEmail}
                    id={id}
                  />
                  {!isInviteSent ? (
                    <Button
                      iconName='x'
                      variant='ghost'
                      color='shadow'
                      disabled={invitedUsers.length === 1}
                      onClick={() => removeInput(id)}
                    />
                  ) : null}
                </div>

                <div className='w-20 px-1 text-[10px] font-semibold uppercase rounded-sm bg-violet-web-100 text-violet-web-900'>
                  {!valid ? 'Invalid Email' : ''}
                </div>
              </div>
            );
          })}

          <div
            className={clsx(
              'flex flex-col pt-1 max-w-96',
              isInviteSent ? 'justify-start' : 'justify-center'
            )}
          >
            <div
              className={clsx(
                'px-1 text-xs font-semibold uppercase rounded-sm mb-2',
                responseMessage.includes('sent')
                  ? 'bg-capri-100 text-capri-900 w-fit'
                  : 'bg-violet-web-100 text-violet-web-900 w-fit'
              )}
            >
              {responseMessage}
            </div>
            {!isInviteSent ? (
              <div className=''>
                <Button
                  color='oceanBlue'
                  variant='ghost'
                  iconName='plus-circle'
                  className='w-fit mt-[-10px]'
                  onClick={addInput}
                >
                  Add Another Email
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        <div>
          <hr className='w-full py-2' />
          {isInviteSent ? (
            <div className='flex justify-end'>
              <Button onClick={() => hideModal()}>Close</Button>
            </div>
          ) : (
            <div className='flex items-center justify-between w-full'>
              <Button color='shadow' variant='ghost' onClick={hideModal}>
                Cancel
              </Button>
              <Button
                disabled={enableSendInvites}
                color='oceanBlue'
                iconName='envelope-simple'
                onClick={inviteUsers}
              >
                Send Invite
              </Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default withModal({ name: MODAL_INVITE_TEAM })(InviteTeamPopup);
